import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import BaseMenu from "../components/BaseMenu";
import Page from "../models/Page";
import Scene from "../models/Scene";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import SceneView from "./SceneView";
import { PagePrintInfo } from "../models/PagePrintInfo";
import BaseModelMap from "../models/BaseModelMap";

import Logger from "../Logger";
import { ConnectionType } from "../models/Connection";
import ConnectionTarget from "./ConnectionTarget";
import Multiplexer from "../models/Multiplexer";
import { itx, noItx } from "../translationItx/itx";
import { EventKey } from "../eventLogger/EventKeys";

const logger = new Logger();

export default function SceneGroupView(props: {
  fixed?: boolean;
  mainPage: Page;
  sceneGroup: BaseModelMap<Scene>;
  print?: PagePrintInfo;
  adjustActive?: boolean;
}) {
  const { fixed, mainPage, sceneGroup, print, adjustActive } = props;

  // Entwurfsobjekte für "neu anlegen" (Popup + Drag&Drop).
  // Nach erfolgreichem Erstellen/Verbinden wird jeweils ein neues Placeholder-Objekt erzeugt.
  const newScene = useRef<Scene>(new Scene());
  const newMultiplexer = useRef<Multiplexer>(new Multiplexer());
  const [edit, setEdit] = useState(false);
  const [editMultiplexer, setEditMultiplexer] = useState(false);
  const thisElement = useRef<HTMLDivElement>(null);

  var newNumbScene = 1;
  var newNameScene = `${itx("components.Scene", "Scene")} ${newNumbScene}`;

  var newTagMultiplexer = state.nameAndTagManager.generateTag(newMultiplexer.current);
  var newNameMultiplexer = `${itx("components.Multiplexer", "Multiplexer")}`;

  // Bei Änderungen in der SceneGroup die Connection-Endpunkte aktualisieren,
  // damit Linien nach Add/Delete/Sort korrekt bleiben.
  useOnChangeWatcher(sceneGroup, () => {
    sceneGroup.forEach((scene) => {
      scene.updateConnectionEndpoints();
      scene.connections.forEach((connection) => {
        connection.updateEndPoints();
      });
    });
    return true;
  });

  // useOnChangeWatcher(state.connectionDisplayMode); // da wird neu gezeichnet und eventuell asugeblendet

  function NewSceneEditor(props: {
    sceneGroup: BaseModelMap<Scene>;
    newSceneNumber: string;
    newSceneName: string;
    onOk: () => void;
    onAbort: () => void;
    newScene: MutableRefObject<Scene | undefined>;
    createNewScene: (name?: string, number?: string) => void;
  }) {
    const { newScene, newSceneNumber, newSceneName, createNewScene } = props;

    const [name, setName] = useState(newSceneName);
    const [number, setNumber] = useState(newSceneNumber);

    const { sceneGroup, onOk, onAbort } = props;

    // check if number is already assingned
    function checkValidNumber(nr: number): boolean {
      var double = false;
      sceneGroup.forEach((scene) => {
        if (scene.sceneNumber == nr) {
          double = true;
        }
      });
      return double;
    }
    // check if name is already assingned
    function checkValidName(name: string): boolean {
      var double = false;

      sceneGroup.forEach((scene) => {
        if (state.noDoubleValue.ciEquals(scene.name, name)) {
          double = true;
        }
      });
      return double;
    }
    return (
      <NewSceneOrMultiplexerEditor
        id="newSceneEditor"
        className="sceneNew"
        headerText={itx("SceneGroupView.createNewScene", "Erstelle eine neue Szene")}
        okButtonId="createSceneButton"
        onAbort={onAbort}
        onOk={() => {
          if (newScene.current) {
            newScene.current.name = name.replaceAll("/", "_");
            newScene.current.sceneNumber = parseInt(number);
            state.sceneGroup.add(newScene.current);
            newScene.current.parent = state.sceneGroup;
            state.eventLogger.addEvent(EventKey.ObjectCreated, {
              objectType: "Scene",
              obj: newScene.current.id + "_" + newScene.current.tag + "_" + newScene.current.name,
            });
            createNewScene(name, number);
            onOk();
          }
        }}
        numberInput={{
          defaultValue: number,
          placeholder: itx("SceneGroupView.number", "Nr"),
          type: "number",
          inputId: "sceneNumber",
          onBlur: (ev) => {
            const value = ev.currentTarget.value;
            if (checkValidNumber(parseInt(value))) {
              alert(
                itx("SceneGroupView.sceneNumberT1AlreadyUsed", `scenen Number ${value} ist bereits vergeben`, {
                  T1: value,
                }),
              );
              ev.currentTarget.value = number;
            } else {
              setNumber(value);
            }
          },
        }}
        nameInput={{
          defaultValue: name,
          inputId: "sceneName",
          onBlur: (ev) => {
            const value = ev.currentTarget.value;
            if (checkValidName(value)) {
              alert(itx("SceneGroupView.sceneNameT1AlreadyUsed", `scenen Namen ${value} ist bereits vergeben`));
              ev.currentTarget.value = name;
            } else {
              setName(value);
            }
          },
        }}
      />
    );
  }

  function NewSceneOrMultiplexerEditor(props: {
    id: string;
    className?: string;
    headerText: string;
    okButtonId: string;
    onAbort: () => void;
    onOk: () => void;
    numberInput: {
      defaultValue: string;
      placeholder: string;
      type: "number" | "text";
      inputId: string;
      onBlur: (ev: React.FocusEvent<HTMLInputElement>) => void;
    };
    nameInput: { defaultValue: string; inputId: string; onBlur: (ev: React.FocusEvent<HTMLInputElement>) => void };
  }) {
    const { id, className, headerText, okButtonId, onAbort, onOk, numberInput, nameInput } = props;
    return (
      <div
        id={id}
        style={{
          padding: "10px",
          height: "20px",
        }}
      >
        <div
          className={className || "sceneNew"}
          style={{ position: "absolute", right: "10px", top: "10px", width: "max-content" }}
        >
          <div className="header2" style={{ borderRadius: "5px 5px 0 0", marginBottom: "5px" }}>
            {headerText}
          </div>
          <div style={{ padding: "10px", width: "100%" }}>
            <input
              className="input"
              type={numberInput.type} // no itx
              id={numberInput.inputId}
              min={1}
              placeholder={numberInput.placeholder}
              maxLength={state.maxCharLimit.tag}
              defaultValue={numberInput.defaultValue}
              style={{ width: "50px" }}
              onBlur={(ev) => {
                numberInput.onBlur(ev);
              }}
            />
            &nbsp;
            <input
              className="input"
              type="text"
              id={nameInput.inputId}
              placeholder={itx("name", "Name")}
              maxLength={state.maxCharLimit.smallText}
              defaultValue={nameInput.defaultValue}
              style={{ width: "120px" }}
              onBlur={(ev) => {
                ev.currentTarget.value = ev.currentTarget.value.replaceAll("/", "_");
                nameInput.onBlur(ev);
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                marginTop: "5px",
              }}
            >
              <button
                className="button"
                onClick={() => {
                  onAbort();
                }}
              >
                {itx("cancle", "cancle")}{" "}
              </button>
              <button
                className="button"
                id={okButtonId}
                onClick={() => {
                  onOk();
                }}
              >
                {itx("ok", "ok")}{" "}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function NewMultiplexerEditor(props: {
    sceneGroup: BaseModelMap<Scene>;
    newMultiplexerTag: string;
    newMultiplexerName: string;
    onOk: () => void;
    onAbort: () => void;
    newMultiplexer: MutableRefObject<Multiplexer | undefined>;
    createNewMultiplexer: (name?: string, number?: string) => void;
  }) {
    const { newMultiplexer, newMultiplexerTag, newMultiplexerName, createNewMultiplexer } = props;

    const [name, setName] = useState(newMultiplexerName);
    const [tag, setTag] = useState(newMultiplexerTag);

    const { sceneGroup, onOk, onAbort } = props;
    // check if number is already assingned
    function checkValidTag(nr: string): boolean {
      var double = false;
      sceneGroup.forEach((scene) => {
        if (scene.tag == nr) {
          double = true;
        }
      });
      return double;
    }
    // check if name is already assingned
    function checkValidName(name: string): boolean {
      var double = false;

      sceneGroup.forEach((scene) => {
        if (state.noDoubleValue.ciEquals(scene.name, name)) {
          double = true;
        }
      });
      return double;
    }

    return (
      <NewSceneOrMultiplexerEditor
        id="newMultiplexerEditor"
        className="sceneNew multiplexerNew"
        headerText={itx("SceneGroupView.createNewMultiplexer", "Erstelle einen neuen Multiplexer")}
        okButtonId="createMultiplexerButton"
        onAbort={onAbort}
        onOk={() => {
          if (newMultiplexer.current) {
            newMultiplexer.current.name = name.replaceAll("/", "_");
            newMultiplexer.current.tag = tag;
            state.sceneGroup.add(newMultiplexer.current);
            newMultiplexer.current.parent = state.sceneGroup;

            state.eventLogger.addEvent(EventKey.ObjectCreated, {
              objectType: "Multiplexer",
              obj: newMultiplexer.current.id + "_" + newMultiplexer.current.tag + "_" + newMultiplexer.current.name,
            });
            createNewMultiplexer(name, tag);
            onOk();
          }
        }}
        numberInput={{
          defaultValue: tag,
          placeholder: itx("tag", "Tag"),
          type: "text",
          inputId: "tag",
          onBlur: (ev) => {
            const value = ev.currentTarget.value;
            if (checkValidTag(value)) {
              alert(
                itx("SceneGroupView.multiplexerTagT1AlreadyUsed", `Multiplexer Tag ${value} ist bereits vergeben`, {
                  T1: value,
                }),
              );
              ev.currentTarget.value = tag;
            } else {
              setTag(value);
            }
          },
        }}
        nameInput={{
          defaultValue: name,
          inputId: "multiplexerName",
          onBlur: (ev) => {
            const value = ev.currentTarget.value;
            if (checkValidName(value)) {
              alert(
                itx("SceneGroupView.multiplexerNameT1AlreadyUsed", `Multiplexer Name ${value} ist bereits vergeben`, {
                  T1: value,
                }),
              );
              ev.currentTarget.value = name;
            } else {
              setName(value);
            }
          },
        }}
      />
    );
  }

  function createNewScene(name?: string, number?: string) {
    if (newScene.current) {
      // Aktuelles Placeholder-Objekt finalisieren und danach ein frisches
      // Placeholder-Objekt für die nächste Erstellung vorbereiten.
      const sceneName = name || newNameScene;
      const sceneNumber = Number(number) || newNumbScene;

      newScene.current.name = sceneName;
      newScene.current.sceneNumber = sceneNumber;

      newScene.current = new Scene({
        name: sceneName,
        sceneNumber: sceneNumber,
      });
    }
  }
  function createNewMultiplexer(name?: string, tag?: string) {
    if (newMultiplexer.current) {
      // Gleiches Muster wie bei Szenen: aktuelles Objekt übernehmen,
      // anschließend ein neues Entwurfsobjekt erzeugen.
      const multiplexerName = name || newNameMultiplexer;
      const multiplexerTag = tag || state.nameAndTagManager.generateTag(newMultiplexer.current);

      newMultiplexer.current.name = multiplexerName;
      newMultiplexer.current.tag = multiplexerTag;

      newMultiplexer.current = new Multiplexer({
        name: multiplexerName,
        tag: multiplexerTag,
      });
    }
  }

  useEffect(() => {
    createNewScene();
    createNewMultiplexer();
    if (thisElement.current) {
      thisElement.current.style.opacity = "1";
    }
  }, []);

  logger.log("render SceneGroupView", sceneGroup);

  const imgSrc = "images/cabinet.png";

  const children: any = [];

  const scenes = new Array<Scene>();
  const multiplexers = new Array<Scene>();
  sceneGroup.sortIntBy("sceneNumber").forEach((obj) => {
    switch (obj.className) {
      case noItx("Scene"): {
        scenes.push(obj);
        break;
      }
    }
  });

  sceneGroup.sortBy("tag").forEach((obj) => {
    switch (obj.className) {
      case "Multiplexer": {
        multiplexers.push(obj);
        break;
      }
    }
  });

  //
  //
  // existing Scenes
  //
  //

  scenes.forEach((scene) => {
    newNumbScene = Math.max(newNumbScene, scene.sceneNumber) + 1;
    newNameScene = `${itx("components.Scene", "Scene")} ${newNumbScene}`;

    children.push(
      <SceneView
        fixed={fixed || false}
        key={scene.id}
        scene={scene}
        mainPage={mainPage}
        print={print}
        adjustActive={adjustActive}
        showConnectorGrab={false}
      />,
    );
  });

  //
  // new Scene
  //

  if (edit) {
    children.push(
      <NewSceneEditor
        sceneGroup={sceneGroup}
        newSceneName={newNameScene}
        newSceneNumber={String(newNumbScene)}
        newScene={newScene}
        createNewScene={createNewScene}
        onAbort={() => {
          setEdit(false);
        }}
        onOk={() => {
          setEdit(false);
        }}
      />,
    );
  }

  if (!edit) {
    children.push(
      <ConnectionTarget
        key="newScene"
        className=""
        page={mainPage}
        target={newScene.current}
        compact={2}
        connectionType={ConnectionType.Scene}
        print={print}
        style={{ border: "inherit" }}
        mouseUp={() => {
          // Drop auf "+ Multiplexer": neuen Multiplexer anlegen und direkt
          // mit der laufenden Zeichenverbindung verbinden.
          if (
            state.currentDrawingConnection.connection &&
            !state.currentDrawingConnection.connection.forbidden &&
            state.currentDrawingConnection.page
          ) {
            const oldScene = newScene.current;
            oldScene.parent = state.sceneGroup;
            state.sceneGroup.add(oldScene);
            state.currentDrawingConnection.connect(oldScene, ConnectionType.Scene);
            state.eventLogger.addEvent(EventKey.ObjectCreated, {
              objectType: "Scene",
              obj: oldScene.id + "_" + oldScene.tag + "_" + oldScene.name,
            });
            createNewScene();
          }
        }}
      >
        <button
          className="button scene"
          id="SceneGroup_addNewScene"
          style={{
            marginTop: "10px",
            borderRadius: "10px 0 0 10px",
            lineHeight: "initial",
            textAlign: "left",
          }}
          onMouseDown={() => {
            setEdit(true);
            setEditMultiplexer(false);
          }}
          onMouseUp={() => {
            logger.log("scene new", state.currentDrawingConnection);
            if (state.currentDrawingConnection.connection) {
              newScene.current.parent = state.sceneGroup;
              state.sceneGroup.add(newScene.current);
              state.eventLogger.addEvent(EventKey.ObjectCreated, {
                objectType: "Scene",
                obj: newScene.current.id + "_" + newScene.current.tag + "_" + newScene.current.name,
              });
              //connect
              state.currentDrawingConnection.connect(newScene.current, ConnectionType.Scene);
              /////////////////////////////////////////////
              createNewScene();
            }
          }}
          disabled={adjustActive}
        >
          <b>+</b> {itx("components.Scene", "Scene")}
        </button>
      </ConnectionTarget>,
    );
  }

  //
  // Multiplexer
  //

  children.push(<div id="dividerSceneAndMultiplexer" style={{ height: "5px" }} />);

  //
  // Existing Multiplexers
  //

  multiplexers.forEach((scene) => {
    children.push(
      <SceneView
        fixed={fixed || false}
        key={scene.id}
        scene={scene}
        mainPage={mainPage}
        print={print}
        adjustActive={adjustActive}
        showConnectorGrab={false}
      />,
    );
  });

  //
  // new Multiplexer
  //
  if (editMultiplexer) {
    children.push(
      <NewMultiplexerEditor
        sceneGroup={sceneGroup}
        newMultiplexerName={newNameMultiplexer}
        newMultiplexerTag={newTagMultiplexer}
        newMultiplexer={newMultiplexer}
        createNewMultiplexer={createNewMultiplexer}
        onAbort={() => {
          setEditMultiplexer(false);
        }}
        onOk={() => {
          setEditMultiplexer(false);
        }}
      />,
    );
  }

  if (!editMultiplexer) {
    children.push(
      <ConnectionTarget
        key="newMultiplexer"
        className=""
        page={mainPage}
        target={newMultiplexer.current}
        compact={2}
        connectionType={ConnectionType.Multiplexer}
        print={print}
        style={{ border: "inherit" }}
        mouseUp={() => {
          if (
            state.currentDrawingConnection.connection &&
            !state.currentDrawingConnection.connection.forbidden &&
            state.currentDrawingConnection.page
          ) {
            const oldMultiplexer = newMultiplexer.current;
            oldMultiplexer.parent = state.sceneGroup;
            state.sceneGroup.add(oldMultiplexer);
            state.eventLogger.addEvent(EventKey.ObjectCreated, {
              objectType: "Multiplexer",
              obj: newMultiplexer.current.id + "_" + newMultiplexer.current.tag + "_" + newMultiplexer.current.name,
            });
            state.currentDrawingConnection.connect(oldMultiplexer, ConnectionType.Multiplexer);
            createNewMultiplexer();
          }
        }}
      >
        <button
          className="button scene multiplexer"
          id="SceneGroup_addNewMultiplexer"
          style={{
            marginTop: "10px",
            borderRadius: "10px 0 0 10px",
            lineHeight: "initial",
          }}
          onMouseDown={() => {
            setEditMultiplexer(true);
            setEdit(false);
          }}
          onMouseUp={() => {
            logger.log("multiplexer new", state.currentDrawingConnection);
            if (state.currentDrawingConnection.connection) {
              newMultiplexer.current.parent = state.sceneGroup;
              state.sceneGroup.add(newMultiplexer.current);
              state.eventLogger.addEvent(EventKey.ObjectCreated, {
                objectType: "Multiplexer",
                obj: newMultiplexer.current.id + "_" + newMultiplexer.current.tag + "_" + newMultiplexer.current.name,
              });
              //connect
              state.currentDrawingConnection.connect(newMultiplexer.current, ConnectionType.Multiplexer);
              /////////////////////////////////////////////
              createNewMultiplexer();
            }
          }}
          disabled={adjustActive}
        >
          <b>+</b> {itx("components.Multiplexer", "Multiplexer")}
        </button>
      </ConnectionTarget>,
    );
  }
  const styleHead: any = {};
  if (adjustActive) {
    styleHead.backgroundColor = "#d4d2d299"; // no itx
  }

  if (state.settings.customerView) {
    return null;
  }

  const style: any = {
    ...styleHead,
    opacity: "0",
    transition: "opacity 1s ease-in-out",
    height: "max-content",
    maxHeight: "calc(100% - 40px)", // has to be because of the header of the page
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
  };

  return (
    <div className="sceneGroup" id="sceneGroupView" ref={thisElement} style={style}>
      <div
        className="sceneGroupHeader"
        id="sceneGroupViewHeader"
        style={{ width: "100%", pointerEvents: "auto", textAlign: "center" }}
      >
        <BaseMenu obj={sceneGroup} />
        {sceneGroup.name}
      </div>
      <div
        id="sceneGroupViewContent"
        style={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          pointerEvents: "auto",
        }}
        onScroll={() => {
          mainPage.recalcConnections.value++;
          mainPage.updateConnections();
        }}
        onWheel={(ev) => {
          ev.preventDefault();
        }}
      >
        <div
          style={{
            width: "100%",
            marginLeft: "0px",
            boxSizing: "border-box",
            paddingLeft: "0px",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
